import React, { Fragment, useState, useEffect } from "react";
import {
  Card,
  Drawer,
  LoadingOverlay,
  ScrollArea,
  Table,
  UnstyledButton,
  Group,
  Text,
  Center,
  Modal,
  useMantineTheme,
  createStyles,
  Button,
} from "@mantine/core";
import { Edit, Trash } from "tabler-icons-react";
import EditIcon from "@mui/icons-material/Edit";
import {
  IconSelector,
  IconChevronDown,
  IconChevronUp,
  IconEye,
  IconX,
} from "@tabler/icons";
import { showNotification } from "@mantine/notifications";
import { customLoader } from "components/utilities/loader";
import Controls from "components/controls/Controls";
import { API } from "utiles/url";
import { DEL_STOCK } from "apollo/mutuations";
import ManageStock from "components/Stock/ManageStock";
import axios from "axios";
import { useMutation } from "@apollo/client";
import { FiEye } from "react-icons/fi";
import { useViewportSize } from "@mantine/hooks";
import ActionButton from "components/controls/ActionButtonNew";

const useStyles = createStyles((theme) => ({
  th: {
    padding: "0 !important",
  },
  control: {
    width: "100%",
    padding: `${theme.spacing.xs}px ${theme.spacing.md}px`,
    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[6]
          : theme.colors.gray[0],
    },
  },
  icon: {
    width: 21,
    height: 21,
    borderRadius: 21,
  },
  thh: {
    color: "#666666",
    fontFamily: "'__Inter_aaf875','__Inter_Fallback_aaf875'",
    fontSize: "10px",
    textTransform: "uppercase",
    fontWeight: "bold",
  },
  nestedTable: {
    backgroundColor:
      theme.colorScheme === "dark"
        ? theme.colors.dark[7]
        : theme.colors.gray[0],
    margin: `${theme.spacing.xs}px 0`,
    padding: theme.spacing.md,
  },
}));

function Th({ children, sortable, sorted, reversed, onSort }) {
  const { classes } = useStyles();
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  return (
    <th className={classes.th}>
      <UnstyledButton
        onClick={sortable ? onSort : null}
        className={classes.control}
      >
        <Group position="apart">
          <Text weight={500} size="sm">
            {children}
          </Text>
          {sortable && (
            <Center className={classes.icon}>
              <Icon size={14} stroke={1.5} />
            </Center>
          )}
        </Group>
      </UnstyledButton>
    </th>
  );
}

const StockDetail = ({ id, fetchParentData }) => {
  const { classes } = useStyles();
  const theme = useMantineTheme();
  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [openedEdit, setOpenedEdit] = useState(false);
  const [editId, setEditId] = useState();
  const [openedDelete, setOpenedDelete] = useState(false);
  const [deleteID, setDeleteID] = useState(null);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [transactionSortBy, setTransactionSortBy] = useState(null);
  const [transactionReverseSortDirection, setTransactionReverseSortDirection] =
    useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      let token = localStorage.getItem("auth_token");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await axios.get(`${API}/products/${id}/stocks`, config);
      if (response.data) {
        setDrivers(response.data);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  const handleTransactionSort = (field, stockId) => {
    const reversed =
      field === transactionSortBy ? !transactionReverseSortDirection : false;
    setTransactionSortBy(field);
    setTransactionReverseSortDirection(reversed);
    setDrivers((prev) =>
      prev.map((stock) =>
        stock.id === stockId
          ? {
              ...stock,
              transactions: sortTransactions(stock.transactions, {
                sortBy: field,
                reversed,
              }),
            }
          : stock
      )
    );
  };

  const sortData = (data, payload) => {
    if (!payload.sortBy) return [...data];
    return [...data].sort((a, b) => {
      const valueA = a[payload.sortBy] || "";
      const valueB = b[payload.sortBy] || "";
      return payload.reversed
        ? valueB.localeCompare(valueA, { sensitivity: "base" })
        : valueA.localeCompare(valueA, { sensitivity: "base" });
    });
  };

  const sortTransactions = (transactions, payload) => {
    if (!payload.sortBy) return [...transactions];
    return [...transactions].sort((a, b) => {
      let valueA = a[payload.sortBy] || "";
      let valueB = b[payload.sortBy] || "";
      if (payload.sortBy === "created_at") {
        valueA = new Date(valueA).getTime();
        valueB = new Date(valueB).getTime();
        return payload.reversed ? valueB - valueA : valueA - valueB;
      }
      if (
        ["quantity", "before_quantity", "after_quantity"].includes(
          payload.sortBy
        )
      ) {
        valueA = Number(valueA) || 0;
        valueB = Number(valueB) || 0;
        return payload.reversed ? valueB - valueA : valueA - valueB;
      }
      return payload.reversed
        ? valueB.localeCompare(valueA, { sensitivity: "base" })
        : valueA.localeCompare(valueB, { sensitivity: "base" });
    });
  };

  const handleEditDriver = (id) => {
    setOpenedEdit(true);
    setEditId(id);
  };

  const handleDelete = (id) => {
    setOpenedDelete(true);
    setDeleteID(id);
  };

  const [delStock] = useMutation(DEL_STOCK);
  const deleteStock = () => {
    delStock({
      variables: { id: deleteID },
      onCompleted() {
        fetchData();
        setOpenedDelete(false);
        setDeleteID(null);
        showNotification({
          color: "green",
          title: "Success",
          message: "Stock Deleted Successfully",
        });
      },
      onError(error) {
        setOpenedDelete(false);
        setDeleteID(null);
        showNotification({
          color: "red",
          title: "Error",
          message: `${error.message}`,
        });
      },
    });
  };

  const toggleRow = (id) => {
    setExpandedRowId(expandedRowId === id ? null : id);
  };
  const { height } = useViewportSize();

  const rows = drivers?.map((row) => (
    <Fragment key={row.id}>
      <tr>
        <td>{row.warehouse?.name || "N/A"}</td>
        <td>{row.product_sku?.sku || "N/A"}</td>
        <td>{row.quantity || 0}</td>
        <td>{row.minimum_stock_level || 0}</td>
        <td>
          <div style={{ display: "flex", gap: "8px" }}>
            <ActionButton
              label="View"
              color="success"
              onClick={() => toggleRow(row.id)}
            >
              <FiEye size={15} />
            </ActionButton>

            <ActionButton
              label="Update"
              onClick={() => handleEditDriver(row.id)}
            >
              <EditIcon style={{ fontSize: "1rem" }} />
            </ActionButton>

            <ActionButton
              label="Delete"
              color="error"
              onClick={() => handleDelete(row.id)}
            >
              <Trash size={15} />
            </ActionButton>
          </div>
        </td>
      </tr>
      {expandedRowId === row.id && (
        <tr>
          <td colSpan={5}>
            <div className={classes.nestedTable}>
              <Group position="apart" mb="xs">
                <Text weight={500} size="sm">
                  Transactions for Stock ID: {row.id}
                </Text>
                <IconX
                  style={{ cursor: "pointer" }}
                  onClick={() => setExpandedRowId(null)}
                  size={14}
                />
              </Group>
              <Table
                highlightOnHover
                horizontalSpacing="md"
                verticalSpacing="xs"
                sx={{ minWidth: "100%" }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#F9F9F9" }}>
                    <Th
                      sortable={false}
                      sorted={transactionSortBy === "id"}
                      reversed={transactionReverseSortDirection}
                      onSort={() => handleTransactionSort("id", row.id)}
                    >
                      <span className={classes.thh}>Transaction type</span>
                    </Th>
                    <Th
                      sortable={false}
                      sorted={transactionSortBy === "quantity"}
                      reversed={transactionReverseSortDirection}
                      onSort={() => handleTransactionSort("quantity", row.id)}
                    >
                      <span className={classes.thh}>Quantity Changed</span>
                    </Th>
                    <Th
                      sortable={false}
                      sorted={transactionSortBy === "before_quantity"}
                      reversed={transactionReverseSortDirection}
                      onSort={() =>
                        handleTransactionSort("before_quantity", row.id)
                      }
                    >
                      <span className={classes.thh}>Before Quantity</span>
                    </Th>
                    <Th
                      sortable={false}
                      sorted={transactionSortBy === "after_quantity"}
                      reversed={transactionReverseSortDirection}
                      onSort={() =>
                        handleTransactionSort("after_quantity", row.id)
                      }
                    >
                      <span className={classes.thh}>After Quantity</span>
                    </Th>
                    <Th
                      sortable={false}
                      sorted={transactionSortBy === "created_at"}
                      reversed={transactionReverseSortDirection}
                      onSort={() => handleTransactionSort("created_at", row.id)}
                    >
                      <span className={classes.thh}>Date</span>
                    </Th>
                  </tr>
                </thead>
                <tbody>
                  {row.transactions?.length > 0 ? (
                    row.transactions.map((transaction) => (
                      <tr key={transaction.id}>
                        <td>{transaction.transactionable?.type}</td>
                        <td>{transaction.quantity}</td>
                        <td>{transaction.before_quantity}</td>
                        <td>{transaction.after_quantity}</td>
                        <td>
                          {new Date(transaction.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5}>
                        <Text weight={500} align="center">
                          No transactions found
                        </Text>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  ));

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <ScrollArea style={{ height: height / 1.8 }} type="auto" offsetScrollbars>
      <div style={{ width: "98%", margin: "auto" }}>
        <Drawer
          opened={openedEdit}
          overlayColor={
            theme.colorScheme === "dark"
              ? theme.colors.dark[9]
              : theme.colors.gray[2]
          }
          overlayOpacity={0.55}
          overlayBlur={3}
          title="Editing Stock"
          padding="xl"
          onClose={() => setOpenedEdit(false)}
          position="bottom"
          size="80%"
        >
          <ManageStock
            setOpenedEdit={setOpenedEdit}
            editId={editId}
            fetchData={fetchData}
            fetchParentData={fetchParentData}
          />
        </Drawer>
        <Card shadow="sm" p="lg">
          <ScrollArea>
            <Table
              highlightOnHover
              horizontalSpacing="md"
              verticalSpacing="xs"
              sx={{ tableLayout: "fixed", minWidth: 700 }}
            >
              <thead>
                <tr style={{ backgroundColor: "#F1F1F1" }}>
                  <Th sortable={false}>
                    <span className={classes.thh}>Warehouse</span>
                  </Th>
                  <Th sortable={false}>
                    <span className={classes.thh}>Product SKU</span>
                  </Th>
                  <Th sortable={false}>
                    <span className={classes.thh}>Quantity</span>
                  </Th>
                  <Th sortable={false}>
                    <span className={classes.thh}>Stock Level</span>
                  </Th>
                  <Th sortable={false}>
                    <span className={classes.thh}>Actions</span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {rows?.length > 0 ? (
                  rows
                ) : (
                  <tr>
                    <td colSpan={5}>
                      <Text weight={500} align="center">
                        Nothing found
                      </Text>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </ScrollArea>
          <Modal
            opened={openedDelete}
            onClose={() => setOpenedDelete(false)}
            title="Warning"
            centered
          >
            <p>Are you sure you want to delete this stock?</p>
            <Group position="right">
              <Button onClick={deleteStock} color="red">
                Delete
              </Button>
            </Group>
          </Modal>
        </Card>
      </div>
    </ScrollArea>
  );
};

export default StockDetail;
